import { Api } from "../../Api";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { ConnectionState, IExalusConnectionService } from "../IExalusConnectionService";
import { ITypedEvent, TypedEvent } from "../../TypedEvent";
import { Event } from '../../Event';
import { IUser, AccessLevel, Gender } from '../Users/IUser';
import { ISessionService, LoginError } from "./ISessionService";
import { DataFrame, Method, Status } from '../../DataFrame';
import { DependencyContainer } from "../../DependencyContainer";
import { User } from "../Users/User";
import { ControllerConfigurationService } from "../Controller/ControllerConfigurationService";
import { IWebApiCacheService } from "../WebApi/IWebApiCacheService";
import { WebApiCacheService } from "../WebApi/WebApiCacheService";

export class SessionService implements ISessionService {
    private _onUserLoggedInEvent: ITypedEvent<IUser> = new TypedEvent<IUser>();
    private _onUserLoggedOutEvent: ITypedEvent<IUser> = new TypedEvent<IUser>();
    public static readonly ServiceName: string = "SessionService";
    private _email: string = "";
    private _password: string = "";
    private _authToken: string = "";
    private _alreadySubsribedToNetworkEvents: boolean = false;
    private _isLoggedIn: boolean = false;
    private _user: IUser | null = null;

    private isLoginError(value: IUser | LoginError): value is LoginError {
        // Enums in TS are numbers at runtime; check both type and that it's a valid enum member
        return typeof value === "number" && LoginError[value] !== undefined;
    }

    public async RestoreSessionAsync(): Promise<boolean> {
        DependencyContainer.Log?.Debug(SessionService.ServiceName, "Creating RestoreSessionAsync");

        if (this._user === undefined || this._user === null || !this._isLoggedIn)
            return false;

        let res = await this.UserLogInAsync(this._email, this._password);

        if (this.isLoginError(res)) {
            if (res == LoginError.WrongAuthData) {
                DependencyContainer.Log?.Error(SessionService.ServiceName, `RestoreSessionAsync failed, user login rejected`);
                throw new Error("Wrong authentication data!");
            }

            DependencyContainer.Log?.Warning(
                SessionService.ServiceName,
                `RestoreSessionAsync did not complete: ${LoginError[res]}`
            );
            return false;
        }

        DependencyContainer.Log?.Debug(SessionService.ServiceName, `RestoreSessionAsync completed successfully`);
        return true;
    }

    public async WaitForSessionCreationAsync(): Promise<void> {
        return new Promise<void>((resolve) => {
            if (this._isLoggedIn && this._user != null) {
                return resolve();
            }

            const h = (user: IUser) => {
                this._onUserLoggedInEvent.Unsubscribe(h);
                resolve();
            };
            this._onUserLoggedInEvent.Subscribe(h);
        });
    }

    OnUserLoggedInEvent(): ITypedEvent<IUser> {
        return this._onUserLoggedInEvent;
    }

    OnUserLoggedOutEvent(): ITypedEvent<IUser> {
        return this._onUserLoggedOutEvent;
    }

    get User(): IUser | null {
        return this._user;
    }

    async UserLogOutAsync(): Promise<void> {
        let logoutRequest = new DataFrame();
        logoutRequest.Resource = "/users/user/logout";
        logoutRequest.Method = Method.Put;
        this._email = "";
        this._password = "";

        const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName)
            .SendAndWaitForResponseAsync(logoutRequest, 20000, false);

        if (result.Status == Status.OK) {
            (this._onUserLoggedOutEvent as TypedEvent<IUser>).Invoke(this._user as IUser);
            DependencyContainer.Log?.Debug(SessionService.ServiceName, "User has been logged out.");
            this._user = null;
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
        }
        else
            DependencyContainer.Log?.Debug(SessionService.ServiceName, "Failed to logout user!");
    }

    async AuthorizeAppAsync(authToken: string): Promise<IUser | LoginError> {
        let connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        const result = await connection.SendAndWaitForResponseAsync<UserData>(new AuthorizeAppRequest(authToken), 20000, false);

        if (result.Status == Status.OK && result.Data != null) {
            const shouldClearCache = !this._isLoggedIn || this._user?.Guid !== result.Data.Guid;
            this._authToken = authToken;
            let user = new User();
            this._user = user;
            user.AccessLevel = result.Data.AccessLevel;
            user.Email = result.Data.Email;
            this._email = result.Data.Email;
            user.Gender = result.Data.Gender;
            user.Guid = result.Data.Guid;
            user.IsAccountOnline = result.Data.IsAccountOnline;
            user.Name = result.Data.Name;
            user.IsActive = result.Data.IsActive;
            user.IsBanned = result.Data.IsBanned;
            user.Phone = result.Data.Phone;
            user.Surname = result.Data.Surname;
            user.SoftwareVersion = result.Data.SoftwareVersion;
            let conf = Api.Get<ControllerConfigurationService>(ControllerConfigurationService.ServiceName);

            let configurationChanged: boolean;
            if (result.Data.ConfigurationTime !== undefined && result.Data.ConfigurationTime != null) {
                ControllerConfigurationService.ConfigurationTimeRetrieved = new Date(result.Data.ConfigurationTime);
                configurationChanged = await conf.CheckIfConfigurationTimeHasChangedAsync(new Date(result.Data.ConfigurationTime));
            }
            else
                configurationChanged = await conf.CheckIfConfigurationHasChangedAsync();

            if (shouldClearCache && !configurationChanged)
                Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

            if (!this._alreadySubsribedToNetworkEvents) {
                this._alreadySubsribedToNetworkEvents = true;

                Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SubscribeTo("/info/users/user/loggedOut", async (data) => {
                    DependencyContainer.Log?.Debug(SessionService.ServiceName, `User logged out: ${JSON.stringify(data.Data)}`);
                    this._isLoggedIn = false;
                    (this._onUserLoggedOutEvent as TypedEvent<IUser>)?.Invoke(this._user as IUser);
                    this._user = null;
                    Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
                });
            }

            DependencyContainer.Log?.Debug(SessionService.ServiceName, `Did logging in succeded?: ${result.Status == Status.OK}`);
            this._isLoggedIn = true;
            (this._onUserLoggedInEvent as TypedEvent<IUser>).Invoke(this._user as IUser);
            return user;
        }
        else if (result.Status === Status.OperationNotPermitted) {
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `App login response: ${result.Data}`);
            return LoginError.AuthDisabled;
        }
        else if (result.Status === Status.ResourceDoesNotExists) {
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `App login response: ${result.Data}`);
            return LoginError.MethodNotSupported;
        }
        else {
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `App login response: ${result.Data}`);
            return LoginError.WrongAuthData;
        }
    }

    async UserLogInAsync(email: string, password: string): Promise<IUser | LoginError> {
        let connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        const result = await connection.SendAndWaitForResponseAsync<UserData>(new LoginUserRequest(email, password), 20000, false);

        if (result.Status == Status.OK && result.Data != null) {
            const shouldClearCache = !this._isLoggedIn || this._user?.Guid !== result.Data.Guid;
            this._email = email;
            this._password = password;
            let user = new User();
            this._user = user;
            user.AccessLevel = result.Data.AccessLevel;
            user.Email = result.Data.Email;
            user.Gender = result.Data.Gender;
            user.Guid = result.Data.Guid;
            user.IsAccountOnline = result.Data.IsAccountOnline;
            user.Name = result.Data.Name;
            user.IsActive = result.Data.IsActive;
            user.IsBanned = result.Data.IsBanned;
            user.Phone = result.Data.Phone;
            user.Surname = result.Data.Surname;
            user.SoftwareVersion = result.Data.SoftwareVersion;
            let conf = Api.Get<ControllerConfigurationService>(ControllerConfigurationService.ServiceName);

            let configurationChanged: boolean;
            if (result.Data.ConfigurationTime !== undefined && result.Data.ConfigurationTime != null) {
                ControllerConfigurationService.ConfigurationTimeRetrieved = new Date(result.Data.ConfigurationTime);
                configurationChanged = await conf.CheckIfConfigurationTimeHasChangedAsync(new Date(result.Data.ConfigurationTime));
            }
            else
                configurationChanged = await conf.CheckIfConfigurationHasChangedAsync();

            if (shouldClearCache && !configurationChanged)
                Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

            if (!this._alreadySubsribedToNetworkEvents) {
                this._alreadySubsribedToNetworkEvents = true;

                Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SubscribeTo("/info/users/user/loggedOut", async (data) => {
                    DependencyContainer.Log?.Debug(SessionService.ServiceName, `User logged out: ${JSON.stringify(data.Data)}`);
                    this._isLoggedIn = false;
                    (this._onUserLoggedOutEvent as TypedEvent<IUser>)?.Invoke(this._user as IUser);
                    this._user = null;
                    Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
                });
            }

            DependencyContainer.Log?.Debug(SessionService.ServiceName, `Did logging in succeded?: ${result.Status == Status.OK}`);
            this._isLoggedIn = true;
            (this._onUserLoggedInEvent as TypedEvent<IUser>).Invoke(this._user as IUser);
            return user;
        }
        else if (result.Status === Status.WrongData) {
            // WrongAuthData is reserved for an explicit rejection of the
            // supplied controller login/password. Transport and controller
            // availability failures must not invalidate saved credentials.
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `User login rejected: ${result.Data}`);
            return LoginError.WrongAuthData;
        }
        else if (result.Status === Status.OperationNotPermitted) {
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `Controller login is disabled: ${result.Data}`);
            return LoginError.AuthDisabled;
        }
        else if (result.Status === Status.ResourceDoesNotExists) {
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `Controller login method is not supported: ${result.Data}`);
            return LoginError.MethodNotSupported;
        }
        else {
            DependencyContainer.Log?.Debug(SessionService.ServiceName, `Controller login failed with status ${result.Status}: ${result.Data}`);
            return LoginError.Unknown;
        }
    }

    GetServiceName(): string {
        return SessionService.ServiceName;
    }
}

export class UserData {
    Guid: string = "";
    Name: string = "";
    Surname: string = "";
    Password: string = "";
    Email: string = "";
    Phone: string = "";
    IsActive: boolean = true;
    IsBanned: boolean = false;
    IsAccountOnline: boolean = false;
    AccessLevel: AccessLevel = AccessLevel.Any;
    Gender: Gender = Gender.Unknown;
    ConfigurationTime: string = "";
    IsGeolocationSet: boolean = false;
    SoftwareVersion: string = "";
    UpdatesChannel: string = "";
}

class UserLoginData {
    constructor(email: string, password: string) {
        this.Email = email;
        this.Password = password;
    }

    Email: string = "";
    Password: string = "";
}

class AuthorizeAppRequest extends DataFrame<string> {
    constructor(authToken: string) {
        super();
        this.Data = authToken;
        this.Resource = "/applications/app/auth";
        this.Method = Method.Put;
    }
}

class LoginUserRequest extends DataFrame<UserLoginData> {
    constructor(email: string, password: string) {
        super();
        this.Data = new UserLoginData(email, password);
        this.Resource = "/users/user/login";
        this.Method = Method.Put;
    }
}
